import { useMutation } from '@platform/api-client';
import { Button } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { EntryList } from '../EntryList.tsx';
import type { Geography } from '../geography/useGeoAreas.ts';
import { usePageContext } from '../usePageContext.ts';
import { amendBodyOf, orderFormValuesOf, type NewOrderFormValues } from './new-order-form.ts';
import { OrderForm, useOrderForm, type Depot } from './OrderForm.tsx';
import type { Companies } from '../companies.ts';
import { STOCK_RESOURCES, type Order } from './orders.ts';

export type EditOrderFormProps = {
  api: TypedClient;
  order: Order;
  currency: string;
  geography: Geography;
  depot: Depot;
  companies: Companies;
  onDone: (orderId: string) => void;
};

export const EditOrderForm = ({
  api,
  order,
  currency,
  geography,
  depot,
  companies,
  onDone,
}: EditOrderFormProps) => {
  const { t, notify, describeError, invalidate } = usePageContext();
  const form = useOrderForm(orderFormValuesOf(order));
  const save = useMutation({
    mutationFn: (values: NewOrderFormValues) =>
      api.patch('/admin/orders/{id}', { path: { id: order.id }, body: amendBodyOf(order.version, values) }),
    onSuccess: async (saved) => {
      notify(t('editOrder.saved', { number: saved.number }));
      await invalidate(...STOCK_RESOURCES);
      onDone(saved.id);
    },
  });

  return (
    <OrderForm
      api={api}
      form={form}
      currency={currency}
      geography={geography}
      depots={[depot]}
      companies={companies}
      submitLabel={save.isPending ? t('editOrder.saving') : t('editOrder.save')}
      submitting={save.isPending}
      errorMessage={save.isError ? (describeError(save.error) ?? null) : null}
      secondaryAction={
        <Button
          variant="ghost"
          onClick={() => {
            onDone(order.id);
          }}
        >
          {t('editOrder.discard')}
        </Button>
      }
      customerChosen
      customer={
        <EntryList
          labelOf={(key) => t(`orders.${key}`)}
          entries={[
            { key: 'customer', value: order.customer.name },
            { key: 'phone', value: order.customer.phone, ltr: true },
          ]}
        />
      }
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          save.mutate(values);
        })(event);
      }}
    />
  );
};
